import React, { useEffect, useRef, useState } from "react";
import api, { formatPHP, formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface } from "@/components/app/Primitives";
import { toast } from "sonner";
import {
    Upload, Download, CheckCircle2, AlertTriangle, XCircle, FileSpreadsheet,
    History, ArrowRight, Clock, Users, CalendarClock, CircleDot,
} from "lucide-react";

/* Data Migration — bring a company's existing HR data into SAHOD.
 * One page, three datasets, done in a recommended order. Every upload is
 * previewed row by row before anything is written. */

const TONE = {
    ok: { icon: CheckCircle2, cls: "text-emerald-600", row: "" },
    warning: { icon: AlertTriangle, cls: "text-amber-600", row: "bg-amber-50/40" },
    error: { icon: XCircle, cls: "text-red-600", row: "bg-red-50/40" },
};

const ICONS = { shifts: Clock, employees: Users, shift_assignments: CalendarClock };

export default function DataMigration() {
    const [datasets, setDatasets] = useState([]);
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [active, setActive] = useState(null);      // dataset key being worked on
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [filter, setFilter] = useState("all");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const load = () => Promise.all([
        api.get("/hris/migration/datasets").then((r) => { setDatasets(r.data.items || []); setStatus(r.data.status || null); }),
        api.get("/hris/migration/history").then((r) => setHistory(r.data.items || [])),
    ]).catch(() => {});
    useEffect(() => { load(); }, []);

    const download = async (ds) => {
        try {
            const r = await api.get(`/hris/migration/template/${ds.key}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = ds.filename; a.click();
            URL.revokeObjectURL(url);
            toast.success(`${ds.title} template downloaded`);
        } catch { toast.error("Could not download the template"); }
    };

    const pickFile = (key) => { setActive(key); setPreview(null); setResult(null); setTimeout(() => fileRef.current?.click(), 0); };

    const upload = async (file) => {
        if (!file || !active) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(`/hris/migration/preview/${active}`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(data);
            setFilter(data.error ? "error" : "all");
            toast.success(`Read ${data.total} row${data.total === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not read that file");
        } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const commit = async () => {
        const importable = preview.total - preview.error;
        if (!importable) return toast.error("Every row has a blocking problem — fix the file and upload again.");
        const label = datasets.find((d) => d.key === active)?.title || "records";
        if (!window.confirm(`Import ${importable} ${label.toLowerCase()} row${importable === 1 ? "" : "s"}?${preview.error ? ` ${preview.error} blocked row${preview.error === 1 ? "" : "s"} will be skipped.` : ""}`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/hris/migration/commit/${active}`, { rows: preview.rows, skip_errors: true });
            setResult({ ...data, dataset: active });
            setPreview(null); load();
            const n = (data.created_count || 0) + (data.updated_count || 0) + (data.scheduled_count || 0);
            toast.success(`${n} row${n === 1 ? "" : "s"} applied`);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Import failed");
        } finally { setBusy(false); }
    };

    const shown = (preview?.rows || []).filter((r) => filter === "all" || r.status === filter);
    const activeDs = datasets.find((d) => d.key === active);

    /* Column set differs per dataset — show what matters for each. */
    const columnsFor = (key) => key === "shifts"
        ? [["Shift", (r) => r.display.name], ["Hours", (r) => r.display.hours],
           ["Workdays", (r) => r.display.workdays], ["Grace", (r) => `${r.display.grace} min`],
           ["Night", (r) => (r.display.night ? "yes" : "—")]]
        : key === "shift_assignments"
        ? [["Employee", (r) => r.display.employee], ["Employee No", (r) => r.display.employee_no],
           ["Shift", (r) => r.display.shift], ["Applies", (r) => r.display.effective]]
        : [["Employee", (r) => r.display.name], ["Employee No", (r) => r.display.employee_no],
           ["Position", (r) => r.display.position], ["Department", (r) => r.display.department],
           ["Monthly", (r) => (r.display.basic_monthly ? formatPHP(r.display.basic_monthly) : "—")]];

    return (
        <div>
            <PageHeader
                title="Data Migration"
                subtitle="Moving from another system? Download a workbook, fill it with your existing data, and upload it back. Every row is checked before anything is saved." />

            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden"
                   onChange={(e) => upload(e.target.files?.[0])} data-testid="mig-file" />

            {/* Progress */}
            {status && (
                <Surface className="p-4 mb-3" data-testid="mig-status">
                    <h3 className="text-sm font-semibold mb-2">What's loaded so far</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                            { label: "Departments", n: status.departments },
                            { label: "Branches", n: status.branches },
                            { label: "Shifts", n: status.shifts },
                            { label: "Employees", n: status.employees },
                            { label: "Without a shift", n: status.employees_without_shift, warn: status.employees_without_shift > 0 },
                        ].map((s) => (
                            <div key={s.label} className="rounded-lg border border-border p-3">
                                <div className={`text-2xl font-bold font-mono ${s.warn ? "text-amber-600" : ""}`}>{s.n ?? 0}</div>
                                <div className="text-xs text-muted-foreground">{s.label}</div>
                            </div>
                        ))}
                    </div>
                    {status.employees_without_shift > 0 && status.shifts > 0 && (
                        <p className="text-xs text-amber-700 mt-2">
                            {status.employees_without_shift} employee{status.employees_without_shift === 1 ? " has" : "s have"} no
                            shift yet — lateness can't be measured for them. Use the Shift Assignments workbook below.
                        </p>
                    )}
                    {status.departments === 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                            Tip: create your departments and branches first (HR → Organization) so employee rows match them by name.
                        </p>
                    )}
                </Surface>
            )}

            {/* Dataset cards */}
            <div className="grid gap-3 md:grid-cols-3">
                {datasets.map((ds) => {
                    const Icon = ICONS[ds.key] || FileSpreadsheet;
                    const isActive = active === ds.key;
                    return (
                        <Surface key={ds.key} className={`p-4 flex flex-col ${isActive ? "ring-2 ring-primary/40" : ""}`}
                                 data-testid={`mig-card-${ds.key}`}>
                            <div className="flex items-center gap-2">
                                <span className="size-6 rounded-full bg-secondary text-xs font-bold flex items-center justify-center shrink-0">{ds.order}</span>
                                <Icon className="size-4 text-primary" />
                                <h3 className="font-semibold">{ds.title}</h3>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">{ds.description}</p>
                            <p className="text-xs text-muted-foreground mt-1.5 italic">{ds.why}</p>
                            <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                                <button className="h-8 px-2.5 rounded-md border border-border text-xs flex items-center gap-1.5"
                                        onClick={() => download(ds)} data-testid={`mig-template-${ds.key}`}>
                                    <Download className="size-3.5" /> Template
                                </button>
                                <button className="h-8 px-2.5 rounded-md bg-primary text-white text-xs font-medium flex items-center gap-1.5"
                                        disabled={busy} onClick={() => pickFile(ds.key)} data-testid={`mig-upload-${ds.key}`}>
                                    <Upload className="size-3.5" /> Upload
                                </button>
                            </div>
                        </Surface>
                    );
                })}
            </div>

            {/* Preview */}
            {preview && activeDs && (
                <div className="space-y-3 mt-4">
                    <Surface className="p-4">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-sm font-semibold mr-1">{activeDs.title}:</span>
                                {[
                                    { k: "all", n: preview.total, label: "rows", cls: "bg-secondary" },
                                    { k: "ok", n: preview.ok, label: "ready", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                                    { k: "warning", n: preview.warning, label: "need a look", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                                    { k: "error", n: preview.error, label: "blocked", cls: "bg-red-50 text-red-700 border-red-200" },
                                ].map((s) => (
                                    <button key={s.k} onClick={() => setFilter(s.k)}
                                            className={`px-3 py-1.5 rounded-md border text-sm ${s.cls} ${filter === s.k ? "ring-2 ring-primary/40" : ""}`}
                                            data-testid={`mig-filter-${s.k}`}>
                                        <b className="font-mono">{s.n}</b> {s.label}
                                    </button>
                                ))}
                            </div>
                            <button className="h-9 px-4 rounded-md bg-primary text-white text-sm font-semibold disabled:opacity-50"
                                    disabled={busy || preview.total === preview.error} onClick={commit} data-testid="mig-commit">
                                Import {preview.total - preview.error} row{preview.total - preview.error === 1 ? "" : "s"}
                                <ArrowRight className="size-4 inline ml-1.5 -mt-0.5" />
                            </button>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2.5">
                            Matched columns: {preview.detected_columns.join(", ")}
                            {preview.ignored_columns?.length > 0 && <span className="block mt-0.5">Ignored: {preview.ignored_columns.join(", ")}</span>}
                        </div>
                    </Surface>

                    <Surface className="p-0 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40">
                                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                        <th className="px-3 py-2.5">Row</th>
                                        {columnsFor(active).map(([label]) => <th key={label} className="px-3 py-2.5">{label}</th>)}
                                        <th className="px-3 py-2.5">Findings</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shown.map((r) => {
                                        const tone = TONE[r.status];
                                        const Icon = tone.icon;
                                        return (
                                            <tr key={r.row} className={`border-t border-border ${tone.row}`} data-testid={`mig-row-${r.row}`}>
                                                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                                                    <Icon className={`size-3.5 inline mr-1 ${tone.cls}`} />{r.row}
                                                </td>
                                                {columnsFor(active).map(([label, get]) => (
                                                    <td key={label} className="px-3 py-2">{get(r)}</td>
                                                ))}
                                                <td className="px-3 py-2">
                                                    {r.action === "update" && !r.errors.length && (
                                                        <div className="text-xs text-blue-700">↻ updates the existing one</div>
                                                    )}
                                                    {r.errors.map((m, i) => <div key={`e${i}`} className="text-xs text-red-700">✕ {m}</div>)}
                                                    {r.warnings.map((m, i) => <div key={`w${i}`} className="text-xs text-amber-700">! {m}</div>)}
                                                    {!r.errors.length && !r.warnings.length && <span className="text-xs text-emerald-700">Looks good</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!shown.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-muted-foreground">No rows in this filter.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </Surface>
                </div>
            )}

            {/* Result */}
            {result && (
                <Surface className="p-6 mt-4" data-testid="mig-result">
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                        <CheckCircle2 className="size-5" /> Import complete
                    </div>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                        {result.created_count > 0 && <span><b className="font-mono">{result.created_count}</b> created</span>}
                        {result.updated_count > 0 && <span><b className="font-mono">{result.updated_count}</b> updated</span>}
                        {result.scheduled_count > 0 && <span><b className="font-mono">{result.scheduled_count}</b> scheduled for a future date</span>}
                        {result.skipped_count > 0 && <span className="text-amber-700"><b className="font-mono">{result.skipped_count}</b> skipped</span>}
                    </div>
                    {result.skipped_count > 0 && (
                        <div className="mt-3 text-xs space-y-1 max-h-52 overflow-auto border border-border rounded-md p-3">
                            {(result.skipped || []).map((s, i) => (
                                <div key={i} className="text-amber-800">Row {s.row} — {s.name}: {s.reason}</div>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2 mt-4">
                        <a className="h-9 px-4 rounded-md bg-primary text-white text-sm leading-9"
                           href={result.dataset === "shifts" ? "/timekeeping/shifts" : "/hris/employees"}>
                            View {result.dataset === "shifts" ? "shifts" : "employees"}
                        </a>
                        <button className="h-9 px-4 rounded-md border border-border text-sm" onClick={() => setResult(null)}>Import something else</button>
                    </div>
                </Surface>
            )}

            {/* History */}
            {history.length > 0 && (
                <Surface className="p-4 mt-4">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2"><History className="size-4" /> Migration history</h3>
                    <div className="space-y-1 text-sm">
                        {history.map((b) => (
                            <div key={b.id} className="flex items-center justify-between border-b border-border last:border-0 py-1.5 gap-3">
                                <span className="flex items-center gap-2 min-w-0">
                                    <CircleDot className="size-3 text-muted-foreground shrink-0" />
                                    <span className="capitalize truncate">{(b.dataset || "employees").replace(/_/g, " ")}</span>
                                </span>
                                <span className="text-xs text-muted-foreground shrink-0">{formatDateTime(b.imported_at)}</span>
                                <span className="font-mono text-xs shrink-0">
                                    {b.created_count > 0 && <span className="text-emerald-700">+{b.created_count}</span>}
                                    {b.updated_count > 0 && <span className="text-blue-700 ml-2">↻{b.updated_count}</span>}
                                    {b.scheduled_count > 0 && <span className="text-indigo-700 ml-2">⏱{b.scheduled_count}</span>}
                                    {b.skipped_count > 0 && <span className="text-amber-700 ml-2">skip {b.skipped_count}</span>}
                                </span>
                            </div>
                        ))}
                    </div>
                </Surface>
            )}
        </div>
    );
}
